// General Liability Coverage Limits Options
export const COVERAGE_LIMITS = [
  { value: "", label: "-" },
  { value: "100000", label: "$100,000" },
  { value: "300000", label: "$300,000" },
  { value: "500000", label: "$500,000" },
  { value: "1000000", label: "$1,000,000" },
  { value: "2000000", label: "$2,000,000" },
  { value: "3000000", label: "$3,000,000" },
  { value: "5000000", label: "$5,000,000" },
];

// Drop the empty placeholder and prepend an explicit "Excluded" entry. Used only
// by the limits a carrier can actually be told to exclude: Products/Completed
// Ops, Personal & Adv. Injury, Medical Expense, Damage To Rented Premises. Each
// Occurrence and General Aggregate cannot be excluded and keep the placeholder.
//
// The exclusion sentinel. Lives here so the dropdown, the read-mode formatter and
// the PATCH payload all reference one literal — a copy in the repository is how
// ``parseInt("Excluded")`` silently became ``null`` on the wire once already.
export const EXCLUDED_LIMIT = "Excluded";

// Two separate leading entries, because "0" and "Excluded" are different values:
// "0" is a real amount (Markel's Deductible enumeration offers a legitimate $0),
// "Excluded" is the carrier being told the coverage does not apply. The exclusion
// sentinel is the literal string — reusing 0 for it only worked because 0 happens
// to be illegal in these four limits, and it made "excluded" indistinguishable
// from "never captured". The backend receives "Excluded" and maps it to each
// carrier's own exclusion vocabulary.
export function withExcluded<T extends { value: string; label: string }>(
  options: T[],
): T[] {
  const rest = options.filter((o) => o.value !== "");
  return [
    { value: "0", label: "-" } as T,
    { value: EXCLUDED_LIMIT, label: "Excluded" } as T,
    ...rest,
  ];
}

export const EMPLOYEE_BENEFITS_OPTIONS = COVERAGE_LIMITS;

// Aggregate Limits Options
export const AGGREGATE_LIMITS = [
  { value: "", label: "-" },
  { value: "200000", label: "$200,000" },
  { value: "600000", label: "$600,000" },
  { value: "1000000", label: "$1,000,000" },
  { value: "2000000", label: "$2,000,000" },
  { value: "3000000", label: "$3,000,000" },
  { value: "4000000", label: "$4,000,000" },
  { value: "5000000", label: "$5,000,000" },
  { value: "10000000", label: "$10,000,000" },
];

// Deductible Options. No empty "-" placeholder - null in the DB is
// rendered as "-" (Excluded, value 0). Same visual model as the other
// MR-289 dropdowns.
export const DEDUCTIBLE_OPTIONS = [
  { value: "0", label: "-" },
  { value: "500", label: "$500" },
  { value: "1000", label: "$1,000" },
  { value: "2500", label: "$2,500" },
  { value: "5000", label: "$5,000" },
  { value: "10000", label: "$10,000" },
];

// Medical Expense Options
export const MEDICAL_EXPENSE_OPTIONS = [
  { value: "", label: "-" },
  { value: "1000", label: "$1,000" },
  { value: "2500", label: "$2,500" },
  { value: "5000", label: "$5,000" },
  { value: "10000", label: "$10,000" },
  { value: "15000", label: "$15,000" },
  { value: "25000", label: "$25,000" },
];

// Fire Damage Options
export const FIRE_DAMAGE_OPTIONS = [
  { value: "", label: "-" },
  { value: "50000", label: "$50,000" },
  { value: "100000", label: "$100,000" },
  { value: "300000", label: "$300,000" },
  { value: "500000", label: "$500,000" },
];

// Deductible Type Options
export const DEDUCTIBLE_TYPE_OPTIONS = [
  { value: "", label: "-" },
  { value: "Per Occurrence", label: "Per Occurrence" },
  { value: "Per Claim", label: "Per Claim" },
  {
    value: "Bodily Injury-Property Damage Per Occurrence",
    label: "BI-PD Per Occurrence",
  },
  {
    value: "Bodily Injury-Property Damage Per Claim",
    label: "BI-PD Per Claim",
  },
  {
    value:
      "Bodily Injury-Property Damage Per Claim, Bodily Injury-Property Damage Per Occurrence",
    label: "BI-PD Per Claim & Per Occurrence",
  },
  { value: "Aggregate", label: "Aggregate" },
];
